import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { DialogTitle } from '@startuptools/ui/joy/DialogTitle';
import { DialogContent } from '@startuptools/ui/joy/DialogContent';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { Button } from '@startuptools/ui/Button';
import { Trans } from '@lingui/react';
import { DocKind } from '../../../../free-templates/types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { StateStepper } from '../../../../components/steppers/state-stepper/StateStepper';
import { StateStepperStep } from '../../../../components/steppers/state-stepper/StateStepperStep';
import {
  GqlCreateIdentityInput,
  GqlDocSystemTagKind,
  GqlDocumentSignatureMethod,
  GqlScriveAuthMethod,
  GqlSeverity,
} from '../../../../graphql/base-types.graphql';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { FormControl } from '@startuptools/ui/FormControl';
import { SelectIdentity } from '@startuptools/business-ui/identity/select/SelectIdentity';
import { IconButton } from '@startuptools/ui/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { DetailState, InputState, isSwedish, JuridicalKind } from '@startuptools/business-ui/identity/types';
import { Stack } from '@startuptools/ui/Stack';
import { ComponentProps, PropsWithChildren } from 'react';
import {
  useCompanySuspenseQuery,
  useCreateIdentityMutation,
  useDocGenScheduleDocGenMutation,
} from '../../../../graphql/react-operations';
import { companyToIdentityInput } from '@startuptools/business-ui/identity/identity-to-input';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { Box } from '@startuptools/ui/Box';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { Input } from '@startuptools/ui/Input';
import { RichTextInput } from '@startuptools/ui/RichTextInput';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Alert } from '@startuptools/ui/Alert';
import { DocGenInput } from '@startuptools/common/api';
import { ScriveDeliveryMethod } from './employment-agreement-dialog.component';
import { DocGenSchedReq, FOOTER_ENG_STARTUPTOOLS } from '../../../../document-generation/types';
import { DateTime } from 'luxon';
import { countryNamesByCode } from '@startuptools/common/common';
import { useOnce } from '@startuptools/common/react-hooks';
import { FileViewDocument } from '../file-view.graphql';

interface Party {
  entity_kind: JuridicalKind;
  name: string;
  identity_number: string | null;
  country: { primary_lang: string; secondary_lang: string };
  address: string;
}

export interface NdaContext extends DocGenInput {
  date: string;
  primary_lang: boolean;
  secondary_lang: true;
  party_1: Party;
  party_2: Party;
  confidential_information_listed_in_schedule_1_primary: string;
  confidential_information_listed_in_schedule_1_secondary: string;
  contract_duration: string;
  purpose: { primary_lang: string; secondary_lang: string };
  two_way: boolean;
}

type FormValues = {
  party1: GqlCreateIdentityInput | undefined | null;
  party2: GqlCreateIdentityInput | undefined | null;
  purpose: string;
  confidential: string;
  duration: number;
  signatory1: GqlCreateIdentityInput | undefined | null;
  signatory2: GqlCreateIdentityInput | undefined | null;
};

const CreateButton: ComponentProps<typeof SelectIdentity>['createButton'] = props => (
  <IconButton color="primary" {...props}>
    <EditIcon />
  </IconButton>
);

const Step = ({ children }: PropsWithChildren) => {
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const handleStepNext = () => {
    if (validatorStatus?.completed === false || validatorStatus?.hasError) {
      return;
    }
    void stepNext();
  };

  return (
    <Stack gap={3}>
      {children}

      <Box>
        <Button
          color={validatorStatus?.completed && !validatorStatus.hasError ? 'primary' : 'neutral'}
          onClick={handleStepNext}
        >
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};

export const NdaDialog = ({
  onClose,
  docKind,
  onOpenDocument,
}: {
  onClose: () => void;
  docKind: DocKind.OneWayNda | DocKind.TwoWayNda;
  onOpenDocument: (docId: string) => void;
}) => {
  const companyId = useCompanyId();
  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });

  const [doSubmit, { loading }] = useDocGenScheduleDocGenMutation({
    refetchQueries: [FileViewDocument],
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [establishIdentity] = useCreateIdentityMutation({
    variables: { companyId, input: companyToIdentityInput(company) },
  });

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      duration: 5,
      purpose: 'ppp',
      confidential: 'ccc',
    },
  });

  useOnce(() => {
    const doOnce = async () => {
      setValue('party1', await establishIdentity().then(res => res.data?.createIdentity));
    };
    void doOnce();
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    const filename = `${docKind === DocKind.TwoWayNda ? 'Two' : 'One'}-Way NDA - ${values.party1!.name} - ${values.party2!.name}.pdf`;
    const party1 = values.party1!;
    const party2 = values.party2!;
    const signatory1 = values.signatory1!;
    const signatory2 = values.signatory2!;

    const res = await doSubmit({
      variables: {
        companyId,
        input: {
          genSchedRequest: {
            companyId: company.id,
            filename,
            lockdown: false,
            kind: GqlDocSystemTagKind.OneWayNda,
            userTags: ['NDA'],
            signature: {
              method: GqlDocumentSignatureMethod.Digital,
              parties: [
                {
                  auth_method_to_sign: isSwedish(signatory1!)
                    ? GqlScriveAuthMethod.SeBankid
                    : GqlScriveAuthMethod.Standard,
                  auth_method_to_view: GqlScriveAuthMethod.Standard,
                  delivery_method: ScriveDeliveryMethod.Email,
                  email: signatory1.email!,
                  signatory_id: party1.id!,
                  proxy_id: signatory1.id !== party1.id ? signatory1.id! : null,
                },
                {
                  auth_method_to_sign: isSwedish(values.signatory2!)
                    ? GqlScriveAuthMethod.SeBankid
                    : GqlScriveAuthMethod.Standard,
                  auth_method_to_view: GqlScriveAuthMethod.Standard,
                  delivery_method: ScriveDeliveryMethod.Email,
                  email: signatory2.email!,
                  signatory_id: party2.id!,
                  proxy_id: signatory2.id !== party2.id ? signatory2.id! : null,
                },
              ],
            },
            docGens: [
              {
                docGenDetails: {
                  templateId: 'agreements/nda.html.j2',
                  footerPath: FOOTER_ENG_STARTUPTOOLS,
                  filename,
                  inputData: {
                    two_way: docKind === DocKind.TwoWayNda,
                    date: DateTime.now().toISODate(),
                    primary_lang: false,
                    secondary_lang: true,
                    party_1: {
                      entity_kind: party1.juridicalKind,
                      name: party1.name,
                      country: {
                        primary_lang: '',
                        secondary_lang: countryNamesByCode.en[party1.countryCode!]!.name,
                      },
                      identity_number: isSwedish(party1) ? party1.identityNumber : null,
                      address: party1.address!,
                    },
                    party_2: {
                      entity_kind: party2.juridicalKind,
                      name: party2.name,
                      country: {
                        primary_lang: '',
                        secondary_lang: countryNamesByCode.en[party2.countryCode!]!.name,
                      },
                      identity_number: isSwedish(party2) ? party2.identityNumber : null,
                      address: party2.address!,
                    },
                    confidential_information_listed_in_schedule_1_primary: '',
                    confidential_information_listed_in_schedule_1_secondary: values.confidential,
                    contract_duration: values.duration.toString(),
                    purpose: {
                      primary_lang: '',
                      secondary_lang: values.purpose,
                    },
                    signatures: [],
                  } satisfies NdaContext,
                  signatureParties: [],
                },
              },
            ],
          } satisfies DocGenSchedReq<NdaContext>,
        },
      },
    });

    const docId = res.data?.docGenScheduleDocGen.id;
    if (docId) {
      onClose();
      onOpenDocument(docId);
    }
  };

  const currentParty1 = watch('party1');
  const currentParty2 = watch('party2');

  return (
    <Modal open onClose={onClose}>
      <ModalDialog minWidth={800}>
        <ModalClose />
        <DialogTitle>
          {docKind === DocKind.OneWayNda && <Trans id="Create One-Way NDA" />}
          {docKind === DocKind.TwoWayNda && <Trans id="Create Two-Way NDA" />}
        </DialogTitle>
        <DialogContent>
          <form id="ndaDialogForm" onSubmit={handleSubmit(onSubmit)}>
            <StateStepper onStepSaved={async () => null} onFinalStep={async () => null}>
              <StateStepperStep
                id="parties"
                label={<Trans id="Parties" />}
                validatorStatus={{
                  completed: Boolean(currentParty1) && Boolean(currentParty2),
                  hasError: false,
                  severity: GqlSeverity.Error,
                }}
              >
                <Step>
                  <Stack gap={3}>
                    <Controller
                      control={control}
                      name="party1"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl required>
                          <FormLabel>
                            {docKind === DocKind.OneWayNda && <Trans id="Party disclosing information" />}
                            {docKind === DocKind.TwoWayNda && <Trans id="Party one" />}
                          </FormLabel>
                          <SelectIdentity
                            juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
                            availableInputStates={{
                              [InputState.System]: false,
                              [InputState.Swedish]: true,
                              [InputState.Foreign]: true,
                              [InputState.CompanyBoard]: false,
                              [InputState.Fictitious]: false,
                            }}
                            detailFieldsSettings={{
                              email: DetailState.Required,
                              address: DetailState.Required,
                              countryCode: DetailState.Required,
                              phone: DetailState.Hidden,
                            }}
                            handleCreate={async identity => {
                              field.onChange(identity);
                              setValue(
                                'signatory1',
                                identity.juridicalKind === JuridicalKind.Person ? identity : undefined,
                              );
                            }}
                            identity={field.value}
                            value={field.value?.id}
                            allowCreation
                            placeholder={<Trans id="Select a company or person" />}
                            multiple={false}
                            createButton={CreateButton}
                          />
                        </FormControl>
                      )}
                    />
                    <Controller
                      control={control}
                      name="party2"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl required>
                          <FormLabel>
                            {docKind === DocKind.OneWayNda && <Trans id="Party receiving information" />}
                            {docKind === DocKind.TwoWayNda && <Trans id="Party two" />}
                          </FormLabel>
                          <SelectIdentity
                            juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
                            availableInputStates={{
                              [InputState.System]: false,
                              [InputState.Swedish]: true,
                              [InputState.Foreign]: true,
                              [InputState.CompanyBoard]: false,
                              [InputState.Fictitious]: false,
                            }}
                            detailFieldsSettings={{
                              email: DetailState.Required,
                              address: DetailState.Required,
                              countryCode: DetailState.Required,
                              phone: DetailState.Hidden,
                            }}
                            handleCreate={async identity => {
                              field.onChange(identity);
                              setValue(
                                'signatory2',
                                identity.juridicalKind === JuridicalKind.Person ? identity : undefined,
                              );
                            }}
                            identity={field.value}
                            value={field.value?.id}
                            allowCreation
                            placeholder={<Trans id="Select a company or person" />}
                            multiple={false}
                            createButton={CreateButton}
                          />
                        </FormControl>
                      )}
                    />
                  </Stack>
                </Step>
              </StateStepperStep>
              <StateStepperStep
                id="purpose"
                label={<Trans id="Purpose" />}
                validatorStatus={{ completed: Boolean(watch('purpose')), hasError: false, severity: GqlSeverity.Error }}
              >
                <Step>
                  <Controller
                    control={control}
                    name="purpose"
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormControl required error={Boolean(fieldState.error)}>
                        <FormLabel>
                          <Trans id="Provide purpose: For the purpose of" />
                        </FormLabel>
                        <Input {...field} required />
                      </FormControl>
                    )}
                  />
                </Step>
              </StateStepperStep>
              <StateStepperStep
                id="confidential"
                label={<Trans id="Confidential" />}
                validatorStatus={{
                  completed: Boolean(watch('confidential')),
                  hasError: false,
                  severity: GqlSeverity.Error,
                }}
              >
                <Step>
                  <Controller
                    control={control}
                    name="confidential"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControl required>
                        <FormLabel>
                          <Trans id="Describe what specific information shall be confidential" />
                        </FormLabel>
                        <RichTextInput {...field} />
                      </FormControl>
                    )}
                  />
                </Step>
              </StateStepperStep>
              <StateStepperStep
                id="duration"
                label={<Trans id="Period of time" />}
                validatorStatus={{
                  completed: Boolean(watch('duration')),
                  hasError: false,
                  severity: GqlSeverity.Error,
                }}
              >
                <Step>
                  <Controller
                    control={control}
                    name="duration"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <FormControl required>
                        <FormLabel>
                          <Trans id="How many years shall the NDA cover" />
                        </FormLabel>
                        <NumericFormatInput {...field} decimalScale={0} endDecorator={<Trans id="years" />} required />
                      </FormControl>
                    )}
                  />
                </Step>
              </StateStepperStep>
              <StateStepperStep
                id="signatures"
                label={<Trans id="Signatures" />}
                validatorStatus={{
                  completed: Boolean(watch('signatory1')) && Boolean(watch('signatory2')),
                  hasError: false,
                  severity: GqlSeverity.Error,
                }}
              >
                {currentParty1 && currentParty2 ? (
                  <Stack gap={3}>
                    <Trans id="Who signed the document?" />

                    <Controller
                      control={control}
                      name="signatory1"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl required>
                          <FormLabel>
                            <Trans id="Representative for {party}" values={{ party: currentParty1.name }} />
                          </FormLabel>
                          <SelectIdentity
                            juridicalKinds={[JuridicalKind.Person]}
                            availableInputStates={{
                              [InputState.System]: false,
                              [InputState.Swedish]: true,
                              [InputState.Foreign]: true,
                              [InputState.CompanyBoard]:
                                currentParty1.juridicalKind === JuridicalKind.Company &&
                                !currentParty1.identityNumber.startsWith('0'),
                              [InputState.Fictitious]: false,
                            }}
                            detailFieldsSettings={{
                              email: DetailState.Required,
                              address: DetailState.Required,
                              countryCode: DetailState.Required,
                              phone: DetailState.Hidden,
                            }}
                            companyBoardOrgNumber={currentParty1.identityNumber}
                            handleCreate={async identity => field.onChange(identity)}
                            identity={field.value}
                            value={field.value?.id}
                            allowCreation
                            placeholder={<Trans id="Select a person" />}
                            multiple={false}
                            createButton={CreateButton}
                          />
                        </FormControl>
                      )}
                    />
                    <Controller
                      control={control}
                      name="signatory2"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl required>
                          <FormLabel>
                            <Trans id="Representative for {party}" values={{ party: currentParty2.name }} />
                          </FormLabel>
                          <SelectIdentity
                            juridicalKinds={[JuridicalKind.Person]}
                            availableInputStates={{
                              [InputState.System]: false,
                              [InputState.Swedish]: true,
                              [InputState.Foreign]: true,
                              [InputState.CompanyBoard]:
                                currentParty2.juridicalKind === JuridicalKind.Company &&
                                !currentParty2.identityNumber.startsWith('0'),
                              [InputState.Fictitious]: false,
                            }}
                            detailFieldsSettings={{
                              email: DetailState.Required,
                              address: DetailState.Required,
                              countryCode: DetailState.Required,
                              phone: DetailState.Hidden,
                            }}
                            companyBoardOrgNumber={currentParty2.identityNumber}
                            handleCreate={async identity => field.onChange(identity)}
                            identity={field.value}
                            value={field.value?.id}
                            allowCreation
                            placeholder={<Trans id="Select a person" />}
                            multiple={false}
                            createButton={CreateButton}
                          />
                        </FormControl>
                      )}
                    />
                    <Box>
                      <Button type="submit" loading={loading}>
                        <Trans id="Create NDA" />
                      </Button>
                    </Box>
                  </Stack>
                ) : (
                  <Alert>
                    <Trans id="Select parties in step 1" />
                  </Alert>
                )}
              </StateStepperStep>
            </StateStepper>
          </form>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
